import axios, {type Method} from 'axios';
import ApiService from '@/services/ApiService';

/**
 * True when `error` is an Axios error with a 403 response — i.e. the current
 * user is authenticated but lacks the permission for this specific
 * resource/action, as opposed to a network failure or an expired session
 * (401, handled separately by ApiService's own interceptor). Widgets that
 * fetch their own data typically check this in their `catchErrorTyped` catch
 * branch and hide themselves (or just the affected part) instead of showing
 * a generic "خطا در دریافت اطلاعات" error state — a missing permission isn't
 * something a retry button can fix.
 */
export const isForbiddenError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 403;

/** True when `error` is an Axios error with a 404 response — the resource no longer exists. */
export const isNotFoundError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

/**
 * True when `error` is an Axios error with a 503 response — this backend's
 * signal specifically for "the upstream provider (e.g. Chatwoot) is down",
 * as opposed to a real app-level failure. Callers use this to show a quiet
 * "offline" state instead of a generic error dialog.
 */
export const isServiceUnavailableError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 503;

export const catchErrorTyped = async <T, E extends new (message?: string) => Error>(
  promise: Promise<T>,
  errorsToCatch?: E[],
): Promise<[undefined, T] | [InstanceType<E>]> => {
  try {
    const data = await promise;
    return [undefined, data] as [undefined, T];
  } catch (error: any) {
    if (!errorsToCatch) {
      return [error];
    }

    if (errorsToCatch.some(e => error instanceof e)) {
      return [error];
    }

    throw error;
  }
};

export const request = async <T>(
  method: Method,
  url: string,
  data?: any,
  params?: Record<string, any>
): Promise<T> => {
  const response = await ApiService.request<T>({
    method,
    url,
    data,
    params,
  });
  return response.data;
};
